import { VOInstrument, VOMelody, VOMelodyNote } from "../model/DataStructure.js";
import { Instrument } from "./Instrument.js";
import { Note } from "./Note.js";
import { Player, PlayerLoadOptions, PlayerState } from "./Player.js";

export interface MelodyPlayerOptions {
    instruments: VOInstrument[];
    onComplete?: (m: Player) => void;
    onTime?: (m: Player) => void;
    onStateChange?: (m: Player) => void;
    stopInstruments: boolean;
    audioContext: AudioContext;
}

export class MidiPlayer implements Player {

    public onTime = e5.Signal.create<[Player]>();
    public onComplete = e5.Signal.create<[Player]>();
    public onStateChange = e5.Signal.create<[Player]>();

    private _options: MelodyPlayerOptions;
    private _targetTime: number = 0;
    private _contextTime: number = 0;
    private _part: Tone.Part;
    private _instruments: VOInstrument[] = [];
    private _state: PlayerState = "paused";
    private _duration: number = 0;
    private _pitch: number = 0;

    private _samplers: Instrument[] = [];
    private _free: Instrument[] = [];
    private _used: Instrument[] = [];

    private _stopInstruments: boolean = true;
    private _melody: VOMelody = null;
    private _context: AudioContext;
    private _speed: number = 1;

    constructor(options: MelodyPlayerOptions) {

        options = e5.object.merge({
        }, options);
        this._options = options;

        this._context = options.audioContext;

        this._stopInstruments = true;// options.stopInstruments;

        this._instruments = options.instruments;

        this._part = new Tone.Part(
            (time, n: any) => {
                const note = n as VOMelodyNote;
                this.trigggerNote(note, time as number);
            },
            []
        );

        if (typeof options.onComplete === "function") {
            this.onComplete.add(options.onComplete);
        }

        if (typeof options.onTime === "function") {
            this.onTime.add(options.onTime);
        }

        if (typeof options.onStateChange === "function") {
            this.onStateChange.add(options.onStateChange);
        }

        e5.Player.onTick.add(() => {
            if (this._state !== "playing") {
                return;
            }

            if (this.time >= this.duration) {
                this.pause();

                this.onTime.dispatch(this);

                this.onComplete.dispatch(this);
            } else {
                this.onTime.dispatch(this);
            }
        });
    }

    private trigggerNote(note: VOMelodyNote, time: number = 0, duration: number = NaN): void {
        if (this._samplers[note.track.index]) {

            let nodeName = Note.transpose(note.name, this._pitch);
            this._samplers[note.track.index].trigger({
                name: nodeName,
                dur: isNaN(duration) ? note.dur / this._speed : duration,
                velocity: note.velocity
            }, time);

            /**
             * Midi to frequency
             * @see https://www.inspiredacoustics.com/en/MIDI_note_numbers_and_center_frequencies
             */
            // let midi = e5.Calc.clamp(note.midi + this._pitch, 0, 127);
            // let name = Tone.Frequency(midi, "midi");
            // this._samplers[note.track.index].trigger({
            //     name: name as any,
            //     dur: isNaN(duration) ? note.dur / this._speed : duration,
            //     velocity: note.velocity
            // }, time);
        }
    }

    public get state(): PlayerState {
        return this._state;
    }

    private setState(state: PlayerState): boolean {
        if (this._state === state) {
            return false;
        }
        this._state = state;
        this.onStateChange.dispatch(this);
        return true;
    }

    public toggle(): void {
        if (this._state === "playing") {
            this.pause();
        } else {
            this.play();
        }
    }

    public get duration(): number {
        if (!this._melody) {
            return 0;
        }
        return this._duration / this.speed;
    }

    public get time(): number {
        if (!this._melody) {
            return 0;
        }

        if (this._state === "paused") {
            return this._targetTime / this.speed;
        }

        let time = this._targetTime + (this._context.currentTime - this._contextTime) * this.speed;
        return e5.Calc.clamp(time / this.speed, 0, this.duration);
    }

    public set time(value: number) {

        value = e5.Calc.clamp(value, 0, this.duration);

        if (this._state === "paused") {
            this._targetTime = value * this.speed;
            this._contextTime = this._context.currentTime;

            this.onTime.dispatch(this);
            return;
        }

        this.stopInstruments();

        this._part.stop(0);
        this._part.cancel();
        this._targetTime = value * this.speed;
        this._contextTime = this._context.currentTime;

        this.playHalfNotes();

        this._part.start(this._contextTime, this._targetTime);
    }

    private playHalfNotes(): void {
        if (this._targetTime > 0) {

            let time = this._targetTime;

            for (const track of this._melody.tracks) {

                for (const note of track.notes) {

                    let start = note.time;
                    let end = note.time + note.dur;

                    if (start <= time
                        && end > time) {
                        let dur = end - time;
                        this.trigggerNote(note, 0, dur / this._speed);
                    }
                }
            }
        }
    }

    public play(): void {
        if (this._state === "playing"
            || !this._melody) {
            return;
        }

        this._contextTime = this._context.currentTime;

        this.playHalfNotes();

        this._part.start(this._contextTime, this._targetTime);

        this.setState("playing");
    }

    public pause(): void {
        if (this._state === "paused") {
            return;
        }

        this._targetTime = this.time * this.speed;
        this._contextTime = this._context.currentTime;

        this._part.stop(0);
        this.setState("paused");
        this.stopInstruments();
    }

    public stop(): void {
        this.setState("paused");
        this._part.cancel(0);
        this.stopInstruments();
        this.time = 0;
    }

    public get pitch(): number {
        return this._pitch;
    }

    public set pitch(value: number) {
        if (this._pitch === value) {
            return;
        }
        this._pitch = value;
    }

    public get speed(): number {
        return this._speed;
    }

    public set speed(value: number) {
        if (this._speed === value) {
            return;
        }

        this._targetTime = this.time * this._speed;
        this._contextTime = this._context.currentTime;

        this._speed = value;

        this._part.playbackRate = value;

        this.onTime.dispatch(this);
    }

    private stopInstruments(): void {
        if (!this._stopInstruments) {
            return;
        }
        this._used.forEach((i) => {
            i.stop();
        });
    }

    public load(melody: VOMelody, options: PlayerLoadOptions = null): void {
        this._melody = melody;

        options = e5.object.merge<PlayerLoadOptions>(
            {
                pitch: 0,
                speed: 1
            }, options || ({} as any));


        this.pitch = options.pitch;
        this.speed = options.speed;

        this.stopInstruments();

        for (let i of this._used) {
            this._free.push(i);
        }
        this._used.splice(0);

        this._targetTime = 0;
        this._contextTime = this._context.currentTime;

        if (this._state === "playing") {
            this._part.stop(0);
            this._part.cancel();
        }

        if (melody) {
            //@ts-ignore
            this._part.clear();

            this._duration = melody.duration;
            this._samplers = [];

            for (let track of melody.tracks) {
                for (let f of this._free) {
                    if (f.instrument.id === track.instrument.id) {
                        this._samplers[track.index] = f;
                        this._free.splice(this._free.indexOf(f), 1);
                        this._used.push(f);
                        break;
                    }
                }

                if (!this._samplers[track.index]) {
                    const i = new Instrument({
                        context: this._options.audioContext,
                        instrument: e5.array.id(this._instruments, track.instrument.id)
                    });
                    this._samplers[track.index] = i;
                    this._used.push(i);
                }

                for (var note of track.notes) {
                    this._part.add(note.time, note);
                }
            }

            if (this._state === "playing") {
                this.time = 0;
            }
        } else {
            this._duration = 0;

            //@ts-ignore
            this._part.clear();

            this.setState("paused");
        }
    }
}